/**
 * Maatfinder - hetdakraam.nl
 *
 * Klant vult de maat in die hij nodig heeft (B x H cm) en optioneel zolderkamer-m².
 * Tool toont per merk de maatcode die bij die maat hoort (zie SPELING_CM) plus het grootste
 * kleinere alternatief, en 1 maat die iets groter is (GROTER_MAX_CM) voor wie net te krap mat.
 * Geen montage-aftrek meer (Ruben 09-10-2026). Bij ingevulde zolderkamer-m² toont hij ook de
 * daglicht-vuistregel uit het Bbl (~10% glasoppervlak van vloeroppervlakte) als indicatie.
 *
 * Glas (28-09-2026, hub @64afd5): per kaart het EFFECTIEF DAGLICHTOPPERVLAK uit het VELUX-
 * productblad, met de bron erbij (`_meta.glas_bron` in dakraam-maten.json). Een maat zonder
 * bladwaarde, of een merk zonder bron (Roto), krijgt geen glasregel en geen daglichtbadge: we
 * tonen nooit een eigen schatting. De vroegere schatting (b-12)×(h-18)×0,85 is weg.
 *
 * Backward compat: oude window.HETDAKRAAM_MATEN_URL blijft werken (Velux only, zonder glas).
 * Nieuwe window.HETDAKRAAM_DAKRAAM_MATEN_URL heeft Velux + Roto + glas_m2 met bron.
 */

(function () {
  "use strict";

  // Klant vult de maat in die hij nodig heeft; wie 134 × 98 invult, ziet UK04 (Ruben 09-10-2026).
  // Tot die dag las de tool de invoer als dakopening en trok hij de montagemaat eraf (VELUX
  // 6 cm breed en 4,5 cm hoog, Roto 6 en 6; hub @83fd81 en @31502e). Daardoor viel juist de
  // exacte maat af. Vult iemand toch een dakopening in, dan krijgt hij het grootste raam dat
  // binnen die maat valt: de VELUX-maten liggen verder uit elkaar dan de montagemaat, dus ook
  // 140 × 102,5 geeft UK04.
  //   passend: het raam is niet groter dan de ingevulde maat, op SPELING_CM na (afronding en
  //            meetonnauwkeurigheid: 133 × 97 geeft ook UK04).
  //   groter:  het raam is hooguit GROTER_MAX_CM groter; dan is er waarschijnlijk net te krap
  //            gemeten. Getoond als "Iets groter dan je maat", met het verschil erbij.
  // Bewust NIET meer gelijk aan MARGE_CM in maatcode-worker/src/maatadvies.js: de chatbot
  // vraagt bij een nieuw dakraam uitdrukkelijk de dakopening en rekent daar met de montagemaat.
  const SPELING_CM = 1;
  const GROTER_MAX_CM = 3;
  // Een raam dat meer dan zoveel kleiner is dan de ingevulde maat, adviseren we niet.
  const VENSTER_BREEDTE_CM = 15;
  const VENSTER_HOOGTE_CM = 25;
  const DAGLICHT_FACTOR = 0.10; // 10%-vuistregel (Bbl) van vloeroppervlakte = indicatief vereist glas-m2
  // Rasterrijen per resultaatkaart; moet gelijk zijn aan "span 6" in maatfinder.css.
  const KAART_RIJEN = 6;

  // Ruimte (ingevulde maat min raam) in hele millimeters, zodat een decimale invoer
  // zoals 133,5 precies op de grens blijft rekenen.
  function ruimteMm(maatCm, raamCm) {
    return Math.round((maatCm - raamCm) * 10);
  }

  // "passend", "groter" of null (valt af)
  function classificeerMaat(m, b, h) {
    const rb = ruimteMm(b, m.breedte);
    const rh = ruimteMm(h, m.hoogte);
    if (rb > VENSTER_BREEDTE_CM * 10 || rh > VENSTER_HOOGTE_CM * 10) return null;
    if (rb >= -SPELING_CM * 10 && rh >= -SPELING_CM * 10) return "passend";
    if (rb >= -GROTER_MAX_CM * 10 && rh >= -GROTER_MAX_CM * 10) return "groter";
    return null;
  }

  // Precies de ingevulde maat, op de speling na: dat is "Jouw maat".
  function isJouwMaat(m, b, h) {
    return Math.abs(ruimteMm(b, m.breedte)) <= SPELING_CM * 10 &&
      Math.abs(ruimteMm(h, m.hoogte)) <= SPELING_CM * 10;
  }

  // 122.5 -> "122,5"
  function cm(n) {
    return String(Math.round(n * 10) / 10).replace(".", ",");
  }

  // 0.59 -> "0,59"
  function m2(n) {
    return Number(n).toFixed(2).replace(".", ",");
  }

  function merkNaam(merk) {
    return merk === "roto" ? "Roto" : "VELUX";
  }

  let DAKRAAM_DATA = null;

  async function loadData() {
    if (DAKRAAM_DATA) return DAKRAAM_DATA;

    const newUrl = window.HETDAKRAAM_DAKRAAM_MATEN_URL;
    const legacyUrl = window.HETDAKRAAM_MATEN_URL;

    if (newUrl) {
      const response = await fetch(newUrl);
      DAKRAAM_DATA = await response.json();
    } else if (legacyUrl) {
      // Legacy fallback: convert old velux-maten.json schema to new shape. Geen glas_m2 en
      // geen glas_bron: dit schema kent geen bladwaarden, dus er verschijnt geen glasregel.
      const response = await fetch(legacyUrl);
      const legacy = await response.json();
      const velux = {};
      for (const [code, [b, h]] of Object.entries(legacy)) {
        velux[code] = {
          breedte_cm: b,
          hoogte_cm: h,
          merk: "velux",
        };
      }
      DAKRAAM_DATA = { velux, roto: {} };
    } else {
      throw new Error("Maatfinder: geen data-URL geconfigureerd.");
    }

    return DAKRAAM_DATA;
  }

  // Glas alleen met bron: een getal > 0 in de data EN een bronlabel voor dat merk in
  // _meta.glas_bron. Anders null, en dan tonen we niets (geen schatting, geen 0).
  function flattenMaten(data, merken) {
    const result = [];
    const bronnen = (data._meta && data._meta.glas_bron) || {};
    for (const merkKey of merken) {
      const matenObj = data[merkKey] || {};
      for (const [code, info] of Object.entries(matenObj)) {
        // Skip _meta-style keys
        if (code.startsWith("_")) continue;
        const merk = info.merk || merkKey;
        const bron = bronnen[merk] || null;
        const heeftGlas = bron && typeof info.glas_m2 === "number" && info.glas_m2 > 0;
        result.push({
          code,
          breedte: info.breedte_cm,
          hoogte: info.hoogte_cm,
          merk,
          glasM2: heeftGlas ? info.glas_m2 : null,
          glasBron: heeftGlas ? bron : null,
        });
      }
    }
    return result;
  }

  // Passend: niet groter dan de ingevulde maat. De eigen maat altijd bovenaan, daarna het
  // grootste raam (op oppervlak alleen kon een groter passend raam de eigen maat verdringen).
  function findMatches(breedte, hoogte, maten) {
    return maten
      .filter(m => classificeerMaat(m, breedte, hoogte) === "passend")
      .map(m => ({ ...m, score: m.breedte * m.hoogte, jouwMaat: isJouwMaat(m, breedte, hoogte) }))
      .sort((a, b) => (Number(b.jouwMaat) - Number(a.jouwMaat)) || (b.score - a.score));
  }

  // Iets groter dan de ingevulde maat (hooguit GROTER_MAX_CM). Het principe van 12-06-2026 blijft:
  // toon de net-niet-maat, maar gelabeld. Dichtstbij eerst: som van de absolute verschillen, zodat
  // 76 × 116 MK06 (2 + 2) geeft en niet FK06 (10 smaller, 2 hoger).
  function findGroterMatches(breedte, hoogte, maten) {
    return maten
      .filter(m => classificeerMaat(m, breedte, hoogte) === "groter")
      .map(m => ({ ...m, score: Math.abs(m.breedte - breedte) + Math.abs(m.hoogte - hoogte), groter: true }))
      .sort((a, b) => a.score - b.score);
  }

  // "VELUX UK04 en Roto 13/9 (134 × 98 cm)": per merk de eerste, één maat als ze gelijk zijn.
  function naamLijst(items) {
    const perMerk = Object.values(topPerMerk(items, 1)).map(l => l[0]);
    const zelfdeMaat = perMerk.every(m => m.breedte === perMerk[0].breedte && m.hoogte === perMerk[0].hoogte);
    const namen = perMerk.map(m => `<strong>${merkNaam(m.merk)} ${m.code}</strong>` +
      (zelfdeMaat ? "" : ` (${m.breedte} × ${m.hoogte} cm)`));
    return namen.join(" en ") + (zelfdeMaat ? ` (${perMerk[0].breedte} × ${perMerk[0].hoogte} cm)` : "");
  }

  // "2 cm breder en 1,5 cm hoger": elke richting waarin het raam groter is, en ook een kleinere
  // maat zodra die buiten de speling valt ("2 cm breder en 18 cm lager"), anders klopt het half.
  function verschilZin(m, breedte, hoogte) {
    const delen = [];
    const deel = (verschil, groter, kleiner) => {
      const mm = Math.round(verschil * 10);
      if (mm > 0) delen.push(`${cm(verschil)} cm ${groter}`);
      else if (mm < -SPELING_CM * 10) delen.push(`${cm(-verschil)} cm ${kleiner}`);
    };
    deel(m.breedte - breedte, "breder", "smaller");
    deel(m.hoogte - hoogte, "hoger", "lager");
    return delen.join(" en ");
  }

  function topPerMerk(matches, n = 2) {
    const byMerk = {};
    for (const m of matches) {
      if (!byMerk[m.merk]) byMerk[m.merk] = [];
      if (byMerk[m.merk].length < n) byMerk[m.merk].push(m);
    }
    return byMerk;
  }

  function calcDaglicht(kamerM2, glasM2) {
    if (!kamerM2 || kamerM2 <= 0) return null;
    if (typeof glasM2 !== "number" || !(glasM2 > 0)) return null; // geen bladwaarde = geen oordeel
    const vereist = kamerM2 * DAGLICHT_FACTOR;
    const dekking = glasM2 / vereist;
    return {
      vereist: Number(vereist.toFixed(2)),
      glas: glasM2,
      dekking,
      voldoet: dekking >= 1,
    };
  }

  function trackEvent(eventName, params) {
    if (window.gtag) {
      window.gtag("event", eventName, params || {});
    }
  }

  function collectionUrl(merk) {
    return merk === "roto" ? "/collections/roto" : "/collections/velux";
  }

  function renderResults(matches, groterMatches, opts) {
    const { breedte, hoogte, kamerM2, merkFilter } = opts;
    const resultsEl = document.getElementById("maatfinder-results");
    const noMatchEl = document.getElementById("maatfinder-no-match");

    if (matches.length === 0 && groterMatches.length === 0) {
      resultsEl.innerHTML = "";
      document.getElementById("maatfinder-output").classList.remove("maatfinder-output--has-results");
      noMatchEl.hidden = false;
      noMatchEl.querySelector("[data-kozijn-maat]").textContent =
        `${cm(breedte)} × ${cm(hoogte)} cm`;
      trackEvent("maatwijzer_no_match", { breedte, hoogte });
      return;
    }

    noMatchEl.hidden = true;
    document.getElementById("maatfinder-output").classList.add("maatfinder-output--has-results");

    // Per merk: de 2 grootste passende maten (de eigen maat staat dus bovenaan) + 1 iets grotere
    const byMerk = topPerMerk(matches, 2);
    const groterByMerk = topPerMerk(groterMatches, 1);
    // Vaste volgorde: VELUX links, Roto rechts. Eerder won het merk met de grootste passende maat
    // de linkerkolom, waardoor de merken per invoer van kant wisselden.
    const merkenInResult = ["velux", "roto"].filter(m => byMerk[m] || groterByMerk[m]);

    let html = "";

    // Resultaat-kop: koppelt de ingevulde maat terug en noemt de maatcode, zodat de klant in
    // één oogopslag ziet dát dit zijn antwoord is (en kan zien dat zijn maat goed verwerkt is).
    // Spant de volle paneelbreedte via grid-column 1/-1.
    const jouwMaten = matches.filter(m => m.jouwMaat);
    const g0 = groterMatches[0];
    const groterZin = g0
      ? `${naamLijst(groterMatches.filter(m => m.breedte === g0.breedte && m.hoogte === g0.hoogte))} is ${verschilZin(g0, breedte, hoogte)}`
      : "";
    // "Beste pasvorm" alleen als het grootste passende raam ook het dichtst bij de maat ligt. Bij
    // 76 × 116 is MK06 (2 + 2 cm groter) dichterbij dan Roto 7/9 (18 cm lager): dan geen oordeel,
    // de kop noemt beide.
    const afstand = m => Math.abs(m.breedte - breedte) + Math.abs(m.hoogte - hoogte);
    const besteBinnen = matches[0] && !(g0 && afstand(g0) < afstand(matches[0])) ? matches[0] : null;
    let headSub;
    if (jouwMaten.length) {
      headSub = `Jouw maat: ${naamLijst(jouwMaten)}.`;
    } else if (matches[0]) {
      headSub = `Geen dakraam heeft precies deze maat. Het grootste dat erbinnen valt: ${naamLijst([matches[0]])}.` +
        (groterZin ? ` Iets groter: ${groterZin}.` : "");
    } else {
      headSub = `Geen dakraam valt binnen deze maat. Het dichtstbij komt ${groterZin}; meet je maat nog eens na.`;
    }
    html += `
      <header class="maatfinder-resulthead">
        <span class="maatfinder-resulthead__eyebrow">✓ Jouw resultaat</span>
        <h2 class="maatfinder-resulthead__title">Dakramen bij jouw maat van <strong>${cm(breedte)} × ${cm(hoogte)} cm</strong></h2>
        <p class="maatfinder-resulthead__sub">${headSub}</p>
      </header>
    `;

    // Bovengrens-check: >100 m² is onrealistisch voor één verblijfsruimte -
    // toon een nette controle-vraag i.p.v. een gek vuistregel-getal, en sla de
    // daglicht-indicatie (summary + badges) over voor deze invoer.
    const daglichtKamer = kamerM2 > 100 ? 0 : kamerM2;

    // Daglicht-vuistregel-summary (if kamerM2 provided)
    if (kamerM2 > 100) {
      html += `
        <aside class="maatfinder-daglichtnorm maatfinder-daglichtnorm--check">
          <p>
            Je gaf <strong>${kamerM2} m²</strong> op. Dat lijkt een groot vloeroppervlak
            voor één ruimte. Klopt dat? De daglicht-vuistregel reken je per afzonderlijke
            verblijfsruimte.
          </p>
        </aside>
      `;
    } else if (daglichtKamer && daglichtKamer > 0) {
      const vereist = m2(daglichtKamer * DAGLICHT_FACTOR);
      const bestMatch = matches[0] || groterMatches[0];
      const bestDaglicht = bestMatch ? calcDaglicht(daglichtKamer, bestMatch.glasM2) : null;
      const tekortAdvies = (bestDaglicht && !bestDaglicht.voldoet)
        ? `<p class="maatfinder-daglichtnorm__advies">Eén dakraam haalt de 10%-vuistregel zelden; combineer er twee of kies een groter model voor genoeg licht.</p>`
        : "";
      html += `
        <aside class="maatfinder-daglichtnorm">
          <h3>Daglicht: vuistregel (Bbl)</h3>
          <p>
            Vuistregel daglicht (Bbl): voor <strong>${daglichtKamer} m²</strong> is richtlijn
            ~<strong>${vereist} m²</strong> daglichtoppervlak. Dit is een indicatie; de exacte toets
            (equivalente oppervlakte, NEN 2057) doet een vakman.
            <a href="/blogs/kennisbank/daglichtnorm-zolderkamer-bouwbesluit">Meer info</a>
          </p>
          ${tekortAdvies}
        </aside>
      `;
    }

    // Per-merk-blok, beide in één gedeeld raster (.maatfinder-merkgrid): kaart 1 van VELUX en
    // kaart 1 van Roto delen hun rijen, dus ze zijn even hoog en code, maat en knop staan op één
    // lijn (Ruben 09-10). --kolom en --rij plaatsen ze daar; op mobiel stapelt de DOM-volgorde.
    html += `<div class="maatfinder-merkgrid${merkenInResult.length > 1 ? " maatfinder-merkgrid--twee" : ""}">`;
    merkenInResult.forEach((merk, kolom) => {
      const merkLabel = merk === "roto" ? "Roto" : "VELUX";
      const merkClass = `maatfinder-merk-block maatfinder-merk-block--${merk}`;

      // Officiële merklogo's (URLs door de sectie gezet); tekst-fallback zonder config
      const logos = window.HETDAKRAAM_BRAND_LOGOS || {};
      const merkHeading = logos[merk]
        ? `<h3 class="maatfinder-merk-block__brand"><img src="${logos[merk]}" alt="${merkLabel}" class="maatfinder-merk-block__logo" width="120" height="40" loading="lazy"></h3>`
        : `<h3>${merkLabel}</h3>`;

      html += `<section class="${merkClass}" style="--kolom: ${kolom + 1}">
        <header class="maatfinder-merk-block__header">
          ${merkHeading}
          <a href="${collectionUrl(merk)}" class="maatfinder-merk-block__link">
            Bekijk hele assortiment
          </a>
        </header>
        <div class="maatfinder-results__grid">`;

      // Dichtstbij eerst: ligt het iets grotere raam dichter bij de maat dan het grootste passende,
      // dan staat het bovenaan, net als in de kop (76 × 116: MK06 vóór FK04, Roto 7/11 vóór 7/9).
      // Jouw maat blijft altijd eerst.
      const passend = byMerk[merk] || [];
      const groter = groterByMerk[merk] || [];
      const groterEerst = groter[0] && passend[0] && !passend[0].jouwMaat &&
        afstand(groter[0]) < afstand(passend[0]);
      const merkItems = groterEerst ? [...groter, ...passend] : [...passend, ...groter];
      merkItems.forEach((m, i) => {
        const isBest = m === besteBinnen;
        const uitgelicht = m.jouwMaat || isBest;
        const daglicht = calcDaglicht(daglichtKamer, m.glasM2);

        let daglichtBadge = "";
        if (daglicht) {
          if (daglicht.voldoet) {
            daglichtBadge = `<span class="maatfinder-badge maatfinder-badge--ok">✓ Genoeg daglicht (indicatie)</span>`;
          } else {
            const tekort = m2(daglicht.vereist - daglicht.glas);
            daglichtBadge = `<span class="maatfinder-badge maatfinder-badge--warn">⚠ Mogelijk te weinig: vuistregel 10%, mist ~${tekort} m²</span>`;
          }
        }

        // Beide merken hebben een maatcode-collection per maat (/collections/velux-mk04,
        // /collections/roto-7-11, …) - live geverifieerd via Admin API 2026-06-12 (Roto-
        // maat-collecties zijn na de consolidatie opnieuw aangemaakt als gefilterde views).
        // Directe maat-link = canon (nooit de generieke merkpagina).
        const productCollectionUrl = merk === "roto"
          ? `/collections/roto-${String(m.code).replace("/", "-")}`
          : `/collections/velux-${String(m.code).toLowerCase()}`;

        // Bovenste badge: "Jouw maat" bij de eigen maat, anders "Beste pasvorm" op de grootste die
        // erbinnen valt. Iets groter = korte pill + één uitlegregel (de lange nowrap-pill liep
        // buiten de kaartranden); hergebruikt de krap-stijl van vóór 09-10.
        const topBadge = m.jouwMaat
          ? '<span class="maatfinder-badge maatfinder-badge--best">Jouw maat</span>'
          : isBest ? '<span class="maatfinder-badge maatfinder-badge--best">Beste pasvorm</span>' : "";
        const groterBadge = m.groter
          ? `<span class="maatfinder-badge maatfinder-badge--krap">Iets groter dan je maat</span>`
          : "";
        const groterNote = m.groter
          ? `<p class="maatfinder-result__krapnote">Dit raam is ${verschilZin(m, breedte, hoogte)} dan de maat die je invulde. Meet je maat nog eens na.</p>`
          : "";

        // Glasregel alleen met bladwaarde en bron (zie flattenMaten); anders niets. Eigen element
        // met een eigen rij in het raster, zodat de Roto-kaart ernaast meegroeit.
        const glasRegel = m.glasM2 != null && m.glasBron
          ? `<p class="maatfinder-result__glas">${m2(m.glasM2)} m² effectief daglichtoppervlak (${m.glasBron})</p>`
          : "";

        // --rij: kaart i van elk merk start op dezelfde rasterrij (rij 1 = de merkkoppen, elke
        // kaart beslaat KAART_RIJEN rijen: daglicht, code, maat, glas, toelichting, knop).
        html += `
          <article class="maatfinder-result ${uitgelicht ? 'maatfinder-result--best' : ''} ${m.groter ? 'maatfinder-result--krap' : ''}" style="--rij: ${2 + i * KAART_RIJEN}">
            ${topBadge}
            ${groterBadge}
            ${daglichtBadge}
            <h4 class="maatfinder-result__code">${merkLabel} ${m.code}</h4>
            <p class="maatfinder-result__size">${m.breedte} × ${m.hoogte} cm</p>
            ${glasRegel}
            ${groterNote}
            <a class="maatfinder-result__cta button button--primary"
               href="${productCollectionUrl}"
               data-maatfinder-cta="${merk}-${m.code}">
              Bekijk dakramen ${m.code}
            </a>
          </article>
        `;
      });

      html += `</div></section>`;
    });
    html += `</div>`;

    // Cross-brand notice (when both Velux and Roto in results)
    if (merkenInResult.length > 1) {
      html += `
        <aside class="maatfinder-merk-warning">
          <p>
            <strong>Let op:</strong> VELUX en Roto gebruiken <em>verschillende maatcodes en sparing-maten</em>.
            Bij vervanging van een VELUX door een Roto (of andersom) moet je de aftimmering
            meestal aanpassen. <a href="/blogs/kennisbank/velux-vs-roto">Lees meer</a>.
          </p>
        </aside>
      `;
    }

    resultsEl.innerHTML = html;

    const best = matches[0] || groterMatches[0];
    trackEvent("maatwijzer_completed", {
      breedte,
      hoogte,
      best_match: best.code,
      best_match_brand: best.merk,
      jouw_maat: Boolean(matches[0] && matches[0].jouwMaat),
      n_matches: matches.length + groterMatches.length,
      kamer_m2: kamerM2 || null,
      merk_filter: merkFilter,
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const breedte = parseFloat(form.querySelector("[name='breedte']").value);
    const hoogte = parseFloat(form.querySelector("[name='hoogte']").value);

    const kamerInput = form.querySelector("[name='kamer_m2']");
    const kamerM2 = kamerInput ? parseFloat(kamerInput.value) || 0 : 0;

    const merkSelect = form.querySelector("[name='merk']");
    const merkFilter = merkSelect ? merkSelect.value : "beide";

    if (!breedte || !hoogte || breedte < 30 || hoogte < 30 || breedte > 200 || hoogte > 200) {
      const errorEl = document.getElementById("maatfinder-error");
      errorEl.hidden = false;
      errorEl.textContent = "Vul realistische maten in (30 - 200 cm).";
      return;
    }
    document.getElementById("maatfinder-error").hidden = true;

    trackEvent("maatwijzer_submitted", { breedte, hoogte, kamer_m2: kamerM2 || null, merk_filter: merkFilter });

    const data = await loadData();
    const merken = merkFilter === "velux" ? ["velux"]
                 : merkFilter === "roto" ? ["roto"]
                 : ["velux", "roto"];
    const allMaten = flattenMaten(data, merken);
    const matches = findMatches(breedte, hoogte, allMaten);
    const groterMatches = findGroterMatches(breedte, hoogte, allMaten);

    renderResults(matches, groterMatches, { breedte, hoogte, kamerM2, merkFilter });

    document.getElementById("maatfinder-output").scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function applyQueryParams(form) {
    // Lees ?b=80&h=120&m=beide uit URL (hero-form-handoff) en vul + submit auto.
    const params = new URLSearchParams(window.location.search);
    const b = params.get("b");
    const h = params.get("h");
    const m = params.get("m");
    if (!b && !h) return false;

    const breedte = form.querySelector("[name='breedte']");
    const hoogte = form.querySelector("[name='hoogte']");
    const merk = form.querySelector("[name='merk']");
    if (breedte && b) breedte.value = b;
    if (hoogte && h) hoogte.value = h;
    if (merk && m && ["beide", "velux", "roto"].includes(m)) merk.value = m;

    // Auto-submit als beide maten zijn ingevuld
    if (breedte && hoogte && breedte.value && hoogte.value) {
      // small timeout zodat de DOMContentLoaded niet vastloopt
      setTimeout(() => form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event("submit", { cancelable: true })), 50);
      return true;
    }
    return false;
  }

  function init() {
    const form = document.getElementById("maatfinder-form");
    if (!form) return;
    form.addEventListener("submit", handleSubmit);
    trackEvent("maatwijzer_started");

    // Hero-form-handoff: vul + submit als query-params aanwezig zijn
    applyQueryParams(form);

    document.addEventListener("click", e => {
      const cta = e.target.closest("[data-maatfinder-cta]");
      if (cta) {
        trackEvent("maatwijzer_to_product", { code: cta.dataset.maatfinderCta });
      }
    });
  }

  // Expose for unit-testing (zie maatfinder.test.js)
  if (typeof window !== "undefined") {
    window.__hetdakraamMaatfinder = {
      SPELING_CM,
      GROTER_MAX_CM,
      classificeerMaat,
      isJouwMaat,
      findMatches,
      findGroterMatches,
      flattenMaten,
      calcDaglicht,
      topPerMerk,
    };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
